import React, { useState } from 'react';
import Icon from '@/components/ui/Icon';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import SPADropdown from '@/components/admin/SysadminSPA/shared/SPADropdown';
import { useSPAToast } from '@/components/admin/SysadminSPA/shared/SPAToast';
import CopySlideDialog, { CopiedSlide } from './CopySlideDialog';
import DeleteSlideDialog from './DeleteSlideDialog';
import { editUrl, shareUrl } from './slideUrls';
import ToggleVisibilityDialog from './ToggleVisibilityDialog';
import type { SlideRow, SlideTableContext } from './types';

interface SlideActionsProps {
  context: SlideTableContext;
  onCopied: (source: SlideRow, copy: CopiedSlide) => void;
  onDeleted: (slide: SlideRow) => void;
  onToggled: (slide: SlideRow) => void;
  slide: SlideRow;
}

type OpenDialog = 'copy' | 'delete' | 'visibility' | null;

// The row's actions in one menu, like the other SPA tables: Edit opens the builder, Copy URL acts
// at once, and the rest confirm in a dialog the menu item opens. Delete stays disabled for a slide
// with images that has been copied, which the server refuses.
const SlideActions = ({
  context, onCopied, onDeleted, onToggled, slide,
}: SlideActionsProps) => {
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
  const spaToast = useSPAToast();

  const copyUrl = () => navigator.clipboard
    .writeText(shareUrl(slide, context))
    .then(() => spaToast.success('Slide URL copied.'));

  const dialogChange = (dialog: OpenDialog) => (open: boolean) => setOpenDialog(open ? dialog : null);

  return (
    <>
      <SPADropdown>
        <SPADropdown.Trigger asChild>
          <SPAButton aria-label={`Actions for ${slide.title}`} size="xs" variant="outline">
            Actions
            <Icon icon="ChevronDown" size={12} />
          </SPAButton>
        </SPADropdown.Trigger>
        <SPADropdown.Content align="end" className="tw-w-48">
          <SPADropdown.Item asChild>
            <a href={editUrl(slide, context)}>Edit</a>
          </SPADropdown.Item>
          <SPADropdown.Item onSelect={() => setOpenDialog('copy')}>Copy</SPADropdown.Item>
          <SPADropdown.Item onSelect={copyUrl}>Copy URL</SPADropdown.Item>
          <SPADropdown.Item onSelect={() => setOpenDialog('visibility')}>
            {slide.hidden ? 'Show' : 'Hide'}
          </SPADropdown.Item>
          <SPADropdown.Separator />
          <SPADropdown.Item
            className="tw-text-spa-error"
            disabled={!slide.deletable}
            onSelect={() => setOpenDialog('delete')}
          >
            <span className="tw-flex tw-flex-col tw-items-start">
              {slide.deletable ? 'Delete' : 'Can’t delete'}
              {!slide.deletable && (
                <span className="tw-text-xs tw-text-spa-tertiary">Copied while it had images</span>
              )}
            </span>
          </SPADropdown.Item>
        </SPADropdown.Content>
      </SPADropdown>

      <CopySlideDialog
        open={openDialog === 'copy'}
        slide={slide}
        onCopied={onCopied}
        onOpenChange={dialogChange('copy')}
      />
      <ToggleVisibilityDialog
        context={context}
        open={openDialog === 'visibility'}
        slide={slide}
        onOpenChange={dialogChange('visibility')}
        onToggled={onToggled}
      />
      <DeleteSlideDialog
        open={openDialog === 'delete'}
        slide={slide}
        onDeleted={onDeleted}
        onOpenChange={dialogChange('delete')}
      />
    </>
  );
};

export default SlideActions;
